import {
  CoralogixRum,
  type CoralogixBrowserSdkConfig,
  type CoralogixOtelWebOptionsInstrumentations,
} from "@coralogix/browser";
import cookieConsentService, { type CookieConsentProvider } from "../services/cookieConsent";

const CORALOGIX_PUBLIC_KEY = "cxtp_OJs2wmEyrq3xbzbpp4Nswd5gVIYeQa";

/**
 * Signals the SDK collects. Enabled explicitly rather than relying on SDK
 * defaults so the captured set is visible here and cannot change underneath us
 * when `@coralogix/browser` is upgraded.
 */
const INSTRUMENTATIONS: CoralogixOtelWebOptionsInstrumentations = {
  errors: true,
  fetch: true,
  xhr: true,
  custom: true,
  interactions: true,
  web_vitals: true,
  resources: true,
  long_tasks: true,
};

function mapEnvName(env: string): string {
  switch (env.toLowerCase()) {
    case "production":
      return "Production";
    case "qa":
      return "QA";
    case "demo":
      return "Demo";
    case "development":
      return "Development";
    default:
      return env;
  }
}

let initialised = false;

/**
 * Initialise the Coralogix RUM SDK.
 *
 * Call this once, as early as possible (before React renders) so that
 * automatic instrumentation captures everything from the start.
 *
 * Environment and version are injected at build time via Vite env vars:
 * - VITE_ENVIRONMENT: "qa" | "production" | "demo" | "development"
 * - VITE_APP_VERSION: git SHA or build number
 */
export function initialiseTelemetry(): void {
  if (initialised) return;

  const rawEnv = import.meta.env.VITE_ENVIRONMENT ?? "development";
  const environment = mapEnvName(rawEnv);
  const version = import.meta.env.VITE_APP_VERSION || "development";
  const application = `CollinsGuestCheckout.${environment}`;

  if (rawEnv === "development") return;

  if (version === "development" || version === "unknown") {
    console.error(
      `[Telemetry] VITE_APP_VERSION is not set for environment "${environment}" (version="${version}"). ` +
        "RUM sessions will not match any Coralogix Release, so Adoption and Crash-Free Rate will be empty.",
    );
  }

  const initConfig: CoralogixBrowserSdkConfig = {
    public_key: CORALOGIX_PUBLIC_KEY,
    application,
    version,
    coralogixDomain: "EU2",
    environment,
    instrumentations: INSTRUMENTATIONS,
    sessionSampleRate: 100,
    sessionRecordingConfig: {
      enable: true,
      autoStartSessionRecording: true,
    },
  };

  try {
    CoralogixRum.init(initConfig);
    initialised = true;
  } catch (err) {
    console.error("[Telemetry] Failed to initialise Coralogix RUM SDK:", err);
  }
}

/**
 * Initialise the SDK as early as possible while respecting cookie consent.
 *
 * When a decision to consent has already been made (for example on
 * `designmynight.com` before the guest was sent here) the SDK starts
 * synchronously — before React renders — so the initial page view and any
 * network requests or errors raised during boot are captured. Otherwise it
 * starts the moment the guest consents.
 *
 * @param provider consent provider to use; defaults to the configured service.
 * @returns a function detaching the consent listener.
 */
export function bootstrapTelemetry(provider: CookieConsentProvider = cookieConsentService): () => void {
  if (provider.hasConsented()) {
    initialiseTelemetry();
    return () => undefined;
  }

  const unsubscribe = provider.onConsentChange((consented) => {
    if (!consented) return;
    initialiseTelemetry();
    unsubscribe();
  });

  return unsubscribe;
}

export function reportTelemetryError(error: Error, attributes?: Record<string, string>): void {
  if (!initialised) return;

  try {
    const labels = attributes?.screenshotId ? { screenshotId: attributes.screenshotId } : undefined;
    CoralogixRum.captureError(error, attributes, labels);
  } catch (err) {
    console.error("[Telemetry] Failed to report error:", err);
  }
}

export function setTelemetryLabels(labels: Record<string, string>): void {
  try {
    CoralogixRum.setLabels(labels);
  } catch (err) {
    console.error("[Telemetry] Failed to set labels:", err);
  }
}
